const initAccountReturn = () => {
  const params = new URLSearchParams(window.location.search);
  const returnTo = params.get('return_to') || params.get('return_url');

  if (!returnTo || returnTo[0] !== '/' || returnTo.startsWith('//')) {
    return;
  }

  document.querySelectorAll('[data-account-return-form]').forEach((form) => {
    let input = form.querySelector('input[name="return_to"]');

    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'return_to';
      form.appendChild(input);
    }

    input.value = returnTo;
  });

  document.querySelectorAll('[data-account-register-link]').forEach((link) => {
    const url = new URL(link.getAttribute('href'), window.location.origin);
    url.searchParams.set('return_to', returnTo);
    link.setAttribute('href', `${url.pathname}${url.search}${url.hash}`);
  });
};

const initTradeApplicationForm = (section) => {
  if (section.dataset.tradeApplicationInitialized === 'true') {
    return;
  }

  const form = section.querySelector('form');
  const configScript = section.querySelector('[data-trade-application-config]');

  if (!form || !configScript) {
    return;
  }

  let config = {};

  try {
    config = JSON.parse(configScript.textContent || '{}');
  } catch (_error) {
    config = {};
  }

  section.dataset.tradeApplicationInitialized = 'true';

  const endpoint = section.dataset.endpoint;
  const customerEmail = section.dataset.customerEmail || '';
  const customerIdInput = section.querySelector('[data-trade-customer-id]');
  const customerId = customerIdInput?.value || '';
  const status = section.querySelector('[data-trade-status]');
  const privacy = section.querySelector('[data-trade-privacy]');
  const submit = form.querySelector('button[type="submit"]');
  const accountStatus = section.querySelector('[data-trade-account-status]');
  const accountGate = section.querySelector('[data-trade-account-gate]');
  const submissionGroups = section.querySelectorAll('[data-trade-submission-fields]');
  const accountHelper = section.querySelector('[data-trade-account-helper]');

  const setStatus = (message, state = '') => {
    status.textContent = message || '';
    status.dataset.state = state;
  };

  const currentType = () => form.querySelector('input[name="request_type"]:checked')?.value || 'trade_access';

  const syncOptionHighlight = (radioName) => {
    form.querySelectorAll(`input[name="${radioName}"]`).forEach((input) => {
      input.closest('.trade-application__option')?.classList.toggle('is-selected', input.checked);
    });
  };

  const syncConditionalFields = () => {
    const type = currentType();
    const requiresAccount = type === 'trade_access' && !customerEmail;
    const canSubmit = !requiresAccount;

    privacy.textContent = config.privacy?.[type] || '';

    if (accountHelper) {
      accountHelper.hidden = type !== 'trade_access' || requiresAccount;
    }

    if (accountGate) {
      accountGate.hidden = !requiresAccount;
    }

    if (accountStatus) {
      accountStatus.value = type === 'project_quote'
        ? 'not_required'
        : (customerId ? 'existing_account' : 'login_required');
    }

    if (customerIdInput) {
      customerIdInput.value = type === 'project_quote' ? '' : customerId;
    }

    submissionGroups.forEach((group) => {
      group.hidden = !canSubmit;
      group.querySelectorAll('input, select, textarea, button').forEach((input) => {
        input.disabled = !canSubmit;
      });
    });

    section.querySelectorAll('[data-trade-conditional]').forEach((panel) => {
      const panelVisible = panel.dataset.tradeConditional === type && canSubmit;
      panel.hidden = !panelVisible;

      panel.querySelectorAll('input, select, textarea').forEach((input) => {
        input.required = panelVisible;
        input.disabled = !panelVisible;
      });
    });
  };

  form.addEventListener('change', (event) => {
    if (event.target.name === 'request_type') {
      syncOptionHighlight('request_type');
      syncConditionalFields();
    }

    if (event.target.name === 'tax_exempt_request') {
      syncOptionHighlight('tax_exempt_request');
    }
  });

  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    setStatus('', '');

    if (!endpoint) {
      setStatus(config.messages?.missingEndpoint, 'error');
      return;
    }

    const type = currentType();
    const email = form.elements.work_email.value.trim();

    if (type === 'trade_access' && !customerEmail) {
      setStatus(config.messages?.loginRequired, 'error');
      return;
    }

    if (type === 'trade_access' && customerEmail && email.toLowerCase() !== customerEmail.toLowerCase()) {
      setStatus(config.messages?.emailMismatch, 'error');
      form.elements.work_email.focus();
      return;
    }

    if (!form.reportValidity()) return;

    submit.disabled = true;
    setStatus(config.messages?.loading, '');

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain;charset=utf-8' },
        body: JSON.stringify(Object.fromEntries(new FormData(form).entries()))
      });

      if (!response.ok) throw new Error('Request failed');

      let result = {};
      try {
        result = await response.json();
      } catch (_error) {
        result = { ok: true };
      }

      if (result && result.ok === false) {
        setStatus(result.message || config.messages?.error, 'error');
        return;
      }

      setStatus(config.messages?.success, 'success');
    } catch (_error) {
      setStatus(config.messages?.error, 'error');
    } finally {
      submit.disabled = false;
    }
  });

  syncOptionHighlight('request_type');
  syncOptionHighlight('tax_exempt_request');
  syncConditionalFields();
};

const initTradeApplicationForms = () => {
  document.querySelectorAll('[data-section-type="trade-application-form"]').forEach(initTradeApplicationForm);
};

class BefloProductsSlider extends HTMLElement {
  static get observedAttributes() {
    return ['data-active-block-index'];
  }

  constructor() {
    super();
    this.selectors = {
      swiper: '[js-swiper]',
      swiperSlide: '[js-swiper-slide]',
      swiperNextEl: '[js-swiper-next-button]',
      swiperPrevEl: '[js-swiper-prev-button]'
    };
    this.attrs = { showNavigation: 'data-show-navigation' };
  }

  connectedCallback() {
    this.setupSwiper();
  }

  disconnectedCallback() {
    this.swiperInstance?.destroy();
  }

  attributeChangedCallback(name, _oldValue, newValue) {
    if (name === 'data-active-block-index' && newValue) {
      this.showBlockSlide(newValue);
    }
  }

  setupSwiper() {
    this.swiperContainer = this.querySelector(this.selectors.swiper);
    if (!this.swiperContainer || !window.flu?.chunks?.swiper) return;

    this.swiperSlides = this.swiperContainer.querySelectorAll(this.selectors.swiperSlide);
    this.enableSwiperLoop = this.swiperSlides.length > 1;
    this.swiperContainer.parentElement?.setAttribute(this.attrs.showNavigation, this.enableSwiperLoop);

    import(window.flu.chunks.swiper).then(({ Swiper, Navigation }) => {
      if (!this.isConnected) return;

      this.swiperInstance = new Swiper(this.swiperContainer, {
        modules: [Navigation],
        grabCursor: true,
        spaceBetween: 12,
        slidesPerView: 1,
        watchSlidesProgress: true,
        loop: this.enableSwiperLoop,
        navigation: {
          nextEl: this.querySelector(this.selectors.swiperNextEl),
          prevEl: this.querySelector(this.selectors.swiperPrevEl)
        },
        breakpoints: {
          720: {
            spaceBetween: 16
          }
        },
        on: {
          afterInit: () => {
            this.swiperSlides = this.swiperContainer.querySelectorAll(this.selectors.swiperSlide);
            this.handleSlideChanged();
          },
          transitionEnd: () => this.handleSlideChanged()
        }
      });
    });
  }

  handleSlideChanged() {
    this.swiperSlides.forEach((slide) => {
      slide.toggleAttribute('inert', !slide.classList.contains('swiper-slide-active'));
    });
  }

  showBlockSlide(slideIndex) {
    if (this.swiperInstance?.slideToLoop) {
      this.swiperInstance.slideToLoop(parseInt(slideIndex, 10));
    }
  }
}

BefloProductsSlider.runtimeOwner = 'theme.js';

if (!customElements.get('products-slider')) {
  customElements.define('products-slider', BefloProductsSlider);
}

class BefloScrollingContent extends HTMLElement {
  connectedCallback() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const marquee = this.querySelector('[js-marquee]');
    const content = this.querySelector('[js-marquee-content]');
    if (!marquee || !content || content.offsetWidth === 0) return;

    const clonedContent = this.tabProofClonedContent(content.cloneNode(true));
    this.resizeObserver = new ResizeObserver(() => {
      const viewport = this.closest('section');
      if (!viewport) return;

      const cloneCount = this.getNeededCloneCount(viewport, marquee, content);
      for (let index = 0; index < cloneCount; index += 1) {
        marquee.dataset.playScrollAnimation = 'false';
        marquee.appendChild(clonedContent.cloneNode(true));
      }
      requestAnimationFrame(() => {
        marquee.dataset.playScrollAnimation = 'true';
      });
    });
    this.resizeObserver.observe(document.documentElement);
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
  }

  getNeededCloneCount(viewport, marquee, content) {
    if (content.offsetWidth < 1) return 1;
    return Math.ceil((viewport.offsetWidth - marquee.offsetWidth) / content.offsetWidth) + 1;
  }

  tabProofClonedContent(clonedContent) {
    clonedContent.querySelectorAll('a, button').forEach((element) => {
      element.setAttribute('tabindex', '-1');
    });
    clonedContent.setAttribute('aria-hidden', 'true');
    return clonedContent;
  }
}

if (!customElements.get('scrolling-content')) {
  customElements.define('scrolling-content', BefloScrollingContent);
}

class BefloShoppableVideos extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.setupAnimation();
    this.events = new AbortController();
    this.signal = { signal: this.events.signal };
    this.carousel = this.querySelector('[js-swiper]');
    this.media = Array.from(this.querySelectorAll('[js-media-container][data-has-video="true"]'))
      .map((container) => ({ container, video: container.querySelector('[js-video]') }))
      .filter(({ video }) => video);

    this.playInteraction = window.matchMedia('(prefers-reduced-motion: reduce)').matches
      ? 'click'
      : this.carousel?.dataset.playInteraction || 'hover';

    this.media.forEach(({ container, video }, index) => {
      container.addEventListener('click', (event) => this.handleMediaClick(event, index), this.signal);

      if (this.playInteraction === 'hover') {
        container.addEventListener('mouseenter', () => {
          if (!this.isMobile()) this.play(index);
        }, this.signal);
        container.addEventListener('mouseleave', () => {
          if (!this.isMobile()) this.pause(index);
        }, this.signal);
      }

      container.querySelector('[js-mute-button]')?.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        video.muted ? this.unmute(index) : this.mute(index);
      }, this.signal);
    });

    this.observer = new IntersectionObserver(([entry]) => {
      if (!entry?.isIntersecting || entry.intersectionRatio < 0.5) {
        this.pauseAll();
      } else if (this.playInteraction === 'hover' && this.isMobile()) {
        this.playActive();
      }
    }, { threshold: [0.3, 0.5] });
    this.observer.observe(this);

    this.setupSwiper();
    document.addEventListener('shopify:block:select', (event) => {
      const slide = event.target.closest('[data-block-index]');
      if (slide && this.contains(slide)) this.swiper?.slideTo(Number(slide.dataset.blockIndex));
    }, this.signal);
  }

  disconnectedCallback() {
    this.events?.abort();
    this.observer?.disconnect();
    this.animationObserver?.disconnect();
    this.swiper?.destroy();
    this.initialized = false;
  }

  setupAnimation() {
    const shouldAnimate = this.classList.contains('animation')
      && !document.documentElement.classList.contains('prefers-reduced-motion');

    if (!shouldAnimate || !('IntersectionObserver' in window)) {
      this.classList.add('is-visible');
      return;
    }

    let delayOffset = 0;
    [
      '.animation--section-introduction > *',
      '.animation--controls',
      '.animation--item'
    ].forEach((selector) => {
      this.querySelectorAll(selector).forEach((item) => {
        item.style.setProperty('--delay-offset-multiplier', delayOffset);
        delayOffset += 1;
      });
    });

    const margin = window.matchMedia('(min-width: 720px)').matches ? 200 : 100;
    const threshold = Math.min(margin / this.offsetHeight, 0.5);
    this.animationObserver = new IntersectionObserver(([entry]) => {
      if (!entry?.isIntersecting) return;
      this.classList.add('is-visible');
      this.animationObserver?.disconnect();
      this.animationObserver = null;
    }, { threshold });
    this.animationObserver.observe(this);
  }

  isMobile() {
    return window.matchMedia('(max-width: 719px)').matches;
  }

  setupSwiper() {
    if (!this.carousel || !window.flu?.chunks?.swiper) return;

    const columns = (name) => Number(this.carousel.dataset[name]) || 1;
    const count = this.querySelectorAll('[js-swiper-slide]').length;
    import(window.flu.chunks.swiper).then(({ Swiper, Navigation }) => {
      if (!this.isConnected) return;
      this.swiper = new Swiper(this.carousel, {
        modules: [Navigation],
        grabCursor: true,
        watchSlidesProgress: true,
        slidesPerView: count > 1 ? 1.05 : 1,
        spaceBetween: 13,
        navigation: {
          nextEl: this.querySelector('[data-next]'),
          prevEl: this.querySelector('[data-prev]')
        },
        breakpoints: {
          720: { slidesPerView: columns('columnsTablet'), spaceBetween: 17 },
          960: { slidesPerView: columns('columnsSmallDesktop'), spaceBetween: 17 },
          1200: { slidesPerView: columns('columnsDesktop'), spaceBetween: 25 }
        },
        on: {
          afterInit: () => this.handleSlideChange(),
          slideChange: () => this.handleSlideChange(),
          breakpoint: () => this.handleSlideChange()
        }
      });
    });
  }

  handleMediaClick(event, index) {
    if (this.playInteraction !== 'click' && !document.body.classList.contains('user-is-tabbing')) return;
    event.preventDefault();
    event.stopPropagation();
    this.playInteraction === 'hover' ? this.playOnly(index) : this.toggle(index);
  }

  handleSlideChange() {
    if (this.playInteraction === 'hover' && this.isMobile()) this.playActive();
    else this.pauseAll();
  }

  playActive() {
    const activeSlide = this.swiper?.slides?.[this.swiper.activeIndex];
    const index = this.media.findIndex(({ container }) => activeSlide?.contains(container));
    if (index >= 0) this.playOnly(index);
  }

  playOnly(index) {
    this.media.forEach((_, mediaIndex) => mediaIndex === index ? this.play(mediaIndex) : this.pause(mediaIndex));
  }

  play(index) {
    const media = this.media[index];
    if (!media) return;
    media.video.play().catch(() => {});
    media.container.dataset.isPlaying = 'true';
  }

  pause(index) {
    const media = this.media[index];
    if (!media) return;
    media.video.pause();
    media.container.dataset.isPlaying = 'false';
  }

  pauseAll() {
    this.media.forEach((_, index) => this.pause(index));
  }

  toggle(index) {
    this.media[index]?.video.paused ? this.play(index) : this.pause(index);
  }

  mute(index) {
    const media = this.media[index];
    if (!media) return;
    media.video.muted = true;
    media.container.dataset.isMuted = 'true';
  }

  unmute(index) {
    const media = this.media[index];
    if (!media) return;
    media.video.muted = false;
    media.container.dataset.isMuted = 'false';
  }
}

BefloShoppableVideos.runtimeOwner = 'theme.js';

if (!customElements.get('shoppable-videos')) {
  customElements.define('shoppable-videos', BefloShoppableVideos);
}

class BefloPromotionTile extends HTMLElement {
  connectedCallback() {
    if (this.promotionTileObserver) return;

    const reveal = () => {
      this.classList.add('is-visible');
      this.promotionTileObserver?.disconnect();
      this.promotionTileObserver = null;
    };

    if (!('IntersectionObserver' in window) || window.Shopify?.designMode) {
      reveal();
      return;
    }

    this.promotionTileObserver = new IntersectionObserver(([entry]) => {
      if (entry?.isIntersecting) reveal();
    });
    this.promotionTileObserver.observe(this);
  }

  disconnectedCallback() {
    this.promotionTileObserver?.disconnect();
    this.promotionTileObserver = null;
  }
}

if (!customElements.get('promotion-tile')) {
  customElements.define('promotion-tile', BefloPromotionTile);
}

const initBefloTheme = () => {
  initAccountReturn();
  initTradeApplicationForms();
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initBefloTheme, { once: true });
} else {
  initBefloTheme();
}

document.addEventListener('shopify:section:load', (event) => {
  event.target.querySelectorAll('[data-section-type="trade-application-form"]').forEach(initTradeApplicationForm);
});
